import gql from "graphql-tag";

import {useCachedEffect} from "@khanacademy/wonder-blocks-data";
import {useGql, useRequestIdBuilder, type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    getLearnMenuProgress,
    getLearnMenuProgressVariables,
} from "./__graphql-types__/getLearnMenuProgress.ts";

// The percentages of each subject that is done.
export type SubjectPercentageMap = Record<string, number>;

/**
 * The maximum number of slugs we want to request in one query. This should be
 * a reasonable number (not the full menu) in order to avoid overloading the
 * server in a single request.
 */
const CHUNK_SIZE = 10;

export const useMenuProgressQuery = (
    variables: getLearnMenuProgressVariables,
    skip?: boolean,
) => {
    // We need to chunk requests in batches to avoid overwhelming the server,
    // so we are deconstructing the useQuery hook here.
    const requestId = useRequestIdBuilder()(MENU_PROGRESS_QUERY, variables, {});
    const fetchProgress = createChunkedProgressPromiseFn(useGql());

    const [result] = useCachedEffect<SubjectPercentageMap>(
        requestId,
        fetchProgress([...(variables.slugs ?? [])]),
        {skip},
    );

    if (result.status === "success") {
        return result.data;
    }

    return {};
};

const createChunkedProgressPromiseFn =
    (gqlFetch: GqlFetchFn) => (slugs: string[]) => () =>
        Promise.all(
            [...chunk(slugs, CHUNK_SIZE)].map((slugsChunk) =>
                gqlFetch<getLearnMenuProgress, getLearnMenuProgressVariables>(
                    MENU_PROGRESS_QUERY,
                    {variables: {slugs: slugsChunk}},
                ),
            ),
        ).then(mapDataToResult);

function* chunk<T>(arr: T[], size: number) {
    for (let i = 0; i < arr.length; i += size) {
        yield arr.slice(i, i + size);
    }
}

const mapDataToResult = (resp: Array<getLearnMenuProgress>) =>
    // Combine the responses from all chunks into a single object
    resp.reduce((acc, {user}) => {
        if (user?.subjectProgressesBySlug) {
            user.subjectProgressesBySlug.forEach((subject) => {
                if (
                    subject?.topic?.slug &&
                    subject.currentMastery &&
                    !(subject.topic.slug in acc)
                ) {
                    acc[subject.topic.slug] =
                        subject.currentMastery.percentage ?? 0;
                }
            });
        }
        return acc;
    }, {} satisfies SubjectPercentageMap);

// TODO(jvoll): https://khanacademy.atlassian.net/browse/LP-10455
// If you change this query you also need to update the backend! The resolver
// is only implemented to return the fields that are currently queried! We
// should instead add a more specific field on User (masteryDataForSlugs?) that
// only returns the data that the learn menu needs to avoid the expense of
// populating everything that could possibly be requested under
// SubjectProgress.
const MENU_PROGRESS_QUERY: GqlOperation<
    getLearnMenuProgress,
    getLearnMenuProgressVariables
> = gqlOp(gql`
    query getLearnMenuProgress($slugs: [String!]) {
        user {
            id
            subjectProgressesBySlug(slugs: $slugs) {
                topic {
                    id
                    slug
                }
                currentMastery {
                    percentage
                }
            }
        }
    }
`);
